import Translator from "./translator";
import { CancelablePromise as Promise } from "./cancelablePromise";
import DutyStatusLog from "../model/DutyStatusLog";
import { Dialog } from "../ui/framework/dialog";
import { DriveStore } from "../store/reducers";
import { MobileActions } from "../store/mobile/actions";
import { HOURS, MINUTES, convertDecimalToTimeString } from "hos-tools";

export type TViolationValidationResult = ViolationValidationResult[];

export const enum RestBreakAlert {
    "OffBreak" = "{0} minute break",
    "OffDutyBreak" = "{0} hour(s) off-duty break",
    "cycleRestart" = "{0} hour restart"
}

export const enum RestBreakViolations {
    "offBreak",
    "offDutyBreak",
    "cycleRestart",
    "sbSplit"
}

export abstract class ViolationValidationResult {

    protected hoursKey = "h";
    protected minutesKey = "{minutes}m";
    private readonly strings = {
        "hours": "hours",
        "minutes": "minutes",
        "seconds": "seconds"
    };

    protected getSplitDuration (duration: number) {
        let durationParts = this.splitDurationToHMS(duration);
        let numToFixedLength = (num: number) => num < 10 ? `0${ num.toFixed() }` : num.toFixed();
        return [numToFixedLength(durationParts[0]), numToFixedLength(durationParts[1]), numToFixedLength(durationParts[2])];
    }

    protected getFormattedDuration(duration: number) {
        let getPartItem = (value: string, title: string) => {
            return `<span class="duration-part"><span class="duration-part__value">${ value }</span><span class="duration-part__title">${ title }</span></span>`;
        };

        let durationArray = this.getSplitDuration(duration);
        let hours = getPartItem(durationArray[0], this.strings["hours"]);
        let minutes = getPartItem(durationArray[1], this.strings["minutes"]);
        let seconds = getPartItem(durationArray[2], this.strings["seconds"]);

        return `<p class="duration-parts">${ hours }${ minutes }${ seconds }</p>`;
    }

    protected translate (translator: Translator, phrases: string[]) {
        let toTranslate = Object.keys(this.strings);

        return translator.translate(toTranslate.concat(phrases))
        .then(translated => {
            toTranslate.forEach((toTranslateStr, index) => {
                this.strings[toTranslateStr] = translated[index];
            });

            return translated.slice(toTranslate.length);
        });
    }

    protected getFormattedTimeString(timeInMs: number, translatedHours: string, translatedMinutes: string) {
        return convertDecimalToTimeString(timeInMs / (1000 * 60 * 60), true).replace("{0}", translatedHours).replace("{1}", translatedMinutes);
    }

    /**
     * Splits a timespan (in milliseconds) into hours, minutes, and seconds
     * @param durationInMs The timespan in milliseconds
     */
    private splitDurationToHMS (durationInMs: number): [number /*hours*/, number /*minutes*/, number /*seconds*/] {
        let seconds = durationInMs / 1000;
        let minutes = seconds / 60;
        let hours = minutes / 60;

        return [~~(hours % 24), ~~(minutes % 60), ~~(seconds % 60)];
    }

    abstract getMessage (translator: Translator): Promise<string>;
    abstract getNoticeBoxMessage (translator: Translator): Promise<string>;
}

export class RestBreakViolationValidationResult extends ViolationValidationResult {
    constructor (
        private readonly log: DutyStatusLog,
        private readonly alertName: string,
        private readonly timeToRestEnd: number,
        private readonly requiredDuration: number,
        private readonly showSplitMessage = false
    ) {
        super();
    }

    getMessage(translator: Translator) {
        let newStatus = this.log.status;
        let title = "Changing your status to {0} will interrupt your {1}.";
        let status = `<span class="noTranslate hosStatusLabel ${ newStatus }">${ newStatus }</span>`;
        let timeLeft = "You have the remaining time to complete your break:";
        let splitNote = this.showSplitMessage ? "Please note that this may invalidate previous split sleeper berth breaks if they were taken during an HOS violation." : "";

        return this.translate(translator, [this.alertName, title, timeLeft, splitNote])
            .then(([restBreakAlertMsg, titleMsg, timeLeftMsg, splitNoteMsg]) => {

                // off break should be in minutes, the rest - in hours
                let violationTime = this.alertName === RestBreakAlert.OffBreak ? this.requiredDuration / MINUTES : this.requiredDuration / HOURS;
                let restBreakAlertString = restBreakAlertMsg.replace("{0}", violationTime.toFixed());
                let titleString = titleMsg.replace(/{(\d+)}/g, (_, index) => [status, `<span class="restBreakViolation">${ restBreakAlertString }</span>`][index]);

                return `<p>${ titleString } ${ this.showSplitMessage ? splitNoteMsg : "" } </p><p>${ timeLeftMsg }${ this.getFormattedDuration(this.timeToRestEnd) }</p>`;
            });
    }

    getNoticeBoxMessage(translator: Translator) {
        let timeLeftString = "{0} left";

        return this.translate(translator, [this.alertName, timeLeftString, this.hoursKey, this.minutesKey])
            .then(([restBreakAlertMsg, timeLeftMsg, translatedHours, translatedMinutes]) => {
                let requiredDurationFormatted = this.alertName === RestBreakAlert.OffBreak ? this.requiredDuration / (60 * 1000) : this.requiredDuration / (60 * 60 * 1000);
                let restBreakAlertString = restBreakAlertMsg.replace("{0}", requiredDurationFormatted.toFixed());
                let timeLeftFormattedMsg = timeLeftMsg.replace("{0}", this.getFormattedTimeString(this.timeToRestEnd, translatedHours, translatedMinutes.replace("{minutes}", "")));

                return `${ timeLeftFormattedMsg }: ${ restBreakAlertString }`;
            });
    }
}

export class CanadianMinimumRestBreakViolationValidationResult extends ViolationValidationResult {
    constructor (private readonly minDuration: number, private readonly actualDuration: number) {
        super();
    }

    getMessage(translator: Translator) {
        let title = "You have rested the following time:";
        let description = `Minimum break time is {0} minutes.`;

        return this.translate(translator, [title, description])
            .then(([titleMsg, descMsg]) => {
                let formattedDescMsg = descMsg.replace("{0}", (this.minDuration / (60 * 1000)).toString());
                return `${ titleMsg }${ this.getFormattedDuration(this.actualDuration) }${ formattedDescMsg }`;
            });
    }

    getNoticeBoxMessage(translator: Translator) {
        let timeStringLeft = "{0} left";
        let offBreakString = RestBreakAlert["OffBreak"];

        return this.translate(translator, [offBreakString, timeStringLeft, this.hoursKey, this.minutesKey])
            .then(([restBreakAlertMsg, timeLeftMsg, translatedHours, translatedMinutes]) => {
                let requiredDurationFormatted = this.minDuration / (60 * 1000);
                let restBreakAlertString = restBreakAlertMsg.replace("{0}", requiredDurationFormatted.toFixed());

                let timeToRestEnd = this.minDuration - this.actualDuration;
                let timeLeftFormattedMsg = timeLeftMsg.replace("{0}", this.getFormattedTimeString(timeToRestEnd, translatedHours, translatedMinutes.replace("{minutes}", "")));

                return `${ timeLeftFormattedMsg }: ${ restBreakAlertString }`;
            });
    }
}

export class SplitSbViolationValidationResult extends ViolationValidationResult {
    constructor (private readonly sbDuration: number, private readonly requiredDuration: number) {
        super();
    }

    getMessage(translator: Translator) {
        let title = "You rested for the following time:";
        let description = "This may not be enough time if you are attempting to split a SB.";

        return this.translate(translator, [title, description]).then(([titleMsg, descMsg]) => `${ titleMsg }${ this.getFormattedDuration(this.sbDuration) }${ descMsg }`);
    }

    getNoticeBoxMessage(translator: Translator) {
        let timeStringLeft = "{0} left";
        let offBreakString = RestBreakAlert["OffDutyBreak"];

        return this.translate(translator, [offBreakString, timeStringLeft, this.hoursKey, this.minutesKey])
            .then(([restBreakAlertMsg, timeLeftMsg, translatedHours, translatedMinutes]) => {
                let requiredDurationFormatted = this.requiredDuration / (60 * 60 * 1000);
                let restBreakAlertString = restBreakAlertMsg.replace("{0}", requiredDurationFormatted.toFixed());

                let timeToRestEnd = this.requiredDuration - this.sbDuration;
                let timeLeftFormattedMsg = timeLeftMsg.replace("{0}", this.getFormattedTimeString(timeToRestEnd, translatedHours, translatedMinutes.replace("{minutes}", "")));

                return `${ timeLeftFormattedMsg }: ${ restBreakAlertString }`;
            });
    }
}

export let getAvailabilityDialog = (store: DriveStore, formattedAlerts, violationTitle, translator: Translator) => {
    let dialog = new Dialog(
        "availability-dialog",
        `<span class="noTranslate">${ formattedAlerts.join("<br>") }</span>`,
        translator,
        `<span class="noTranslate">${ violationTitle }</span>`,
        [{
            id: "availability-ok",
            text: "OK",
            callback: () => {
                dialog.remove();
                store.dispatch(MobileActions.setNewUpcomingViolation(false));
            }
        }],
        {
            closable: false,
            isSelfClosing: 20,
            isNightMode: store.getState().data.nightMode
        }
    );

    return dialog;
};

export let getDrivingInViolationDialog = (store: DriveStore, violationTitle, violationNameList, cancelCallback, okCallback, translator: Translator) => {
    let alertDialog = new Dialog(
        "drivingInViolationDialog",
        `${ violationTitle }<ul class='restBreakViolations noTranslate'><li>${ violationNameList.join("</li><li>") }</li></ul>`,
        translator,
        "Warning",
        [
            [
                {
                    id: "drivingInViolationDialog-cancel",
                    text: "Cancel",
                    className: "button_secondary",
                    callback: () => {
                        alertDialog.remove();
                        cancelCallback();
                    }
                },
                {
                    id: "drivingInViolationDialog-ok",
                    text: "Proceed",
                    callback: () => {
                        alertDialog.remove();
                        okCallback();
                    }
                }
            ]
        ],
        {
            closable: false,
            intervenient: true,
            isNightMode: store.getState().data.nightMode
        }
    );

    return alertDialog;
};

export let getBreakInterruptionDialog = (store: DriveStore, messages, question, cancelCallback, okCallback, translator: Translator) => {
    let alertDialog = new Dialog(
        "restBreakWarningDialog",
        `<span class="noTranslate">${ messages.map(message => `<p>${ message }</p>`).join("<div class='messages-divider'></div>") }</span><p>${ question }</p></div>`,
        translator,
        "Break Interruption",
        [
            [
                {
                    id: "restBreakDialog-no",
                    text: "No",
                    className: "button_secondary",
                    callback: () => {
                        alertDialog.remove();
                        cancelCallback();
                    }
                },
                {
                    id: "restBreakDialog-yes",
                    text: "Yes",
                    callback: () => {
                        alertDialog.remove();
                        okCallback();
                    }
                }
            ]
        ],
        {
            closable: false,
            intervenient: true,
            isNightMode: store.getState().data.nightMode
        }
    );

    return alertDialog;
};